import type { ChartRequest, ConversationItem } from './aiChatTypes';
import { getChartTransformer } from './tools/llmToolsCatalog';

/**
 * Re-derives chart data from historical tool output messages.
 *
 * During live streaming, chart transformers run on tool outputs and the resulting
 * ChartRequest[] is attached to the assistant message in Redux. That data is never
 * sent to the backend, so it disappears when the conversation is reloaded from history.
 *
 * The raw tool output payloads ARE stored on the backend. This function re-runs the
 * same transformers against those historical items and reattaches the charts to the
 * corresponding assistant message, so the existing render path picks them up naturally.
 *
 * Handles two backend shapes:
 * - Tool output includes `content.name` directly (name present on the output item)
 * - Tool output only has `content.callId`; the tool name must be looked up from the
 *   preceding `function_call` item that shares the same callId
 *
 * Also handles `output` arriving as a JSON string rather than a parsed object. The gateway's
 * snake_case `call_id` is stored as `callId` when the history is parsed (aiChatHistory.ts).
 * Items without content, such as the gateway's unknown items, are passed over.
 *
 * Note: conversations saved before the `render_chart` refactor (which removed
 * `render_report_visualization`) will not replay charts for those legacy tool calls.
 * The underlying data is no longer available at replay time, so this is accepted
 * migration behavior — charts from pre-migration sessions will be absent on reload.
 */

export function attachChartsToHistoricalMessages(items: ConversationItem[]): ConversationItem[] {
  // Build callId → tool name from function_call items so we can resolve output items
  // that carry a callId but no name of their own.
  const callIdToToolName: Record<string, string> = {};
  for (const item of items) {
    const callId = item.content?.callId;
    const name = item.content?.name;
    if (item.type === 'function_call' && callId && name) {
      callIdToToolName[callId] = name;
    }
  }

  const pendingCharts: ChartRequest[] = [];

  return items.map((item) => {
    const callId = item.content?.callId;
    const toolName = item.content?.name ?? (callId ? callIdToToolName[callId] : undefined);
    const transformer = toolName === undefined ? undefined : getChartTransformer(toolName);

    const rawOutput = item.content?.output;

    if (transformer && rawOutput !== undefined) {
      let output: unknown = rawOutput;
      if (typeof rawOutput === 'string') {
        try {
          output = JSON.parse(rawOutput);
        } catch {
          // Pass it as-is and let the transformer decide. The parse error quotes the output, which
          // holds the user's data, so only the tool name is reported.
          assert(false, 'AI chat tool output is not valid JSON', { toolName });
        }
      }

      // This runs in a reducer, where a throw would send the user to the error page.
      try {
        const result = transformer(output);
        if (result) {
          if (Array.isArray(result)) {
            pendingCharts.push(...result);
          } else {
            pendingCharts.push(result);
          }
        }
      } catch {
        assert(false, 'AI chat chart replay failed', { toolName });
      }
    }

    if (item.role === 'assistant' && item.type === 'message') {
      if (pendingCharts.length > 0 && !item.content?.charts?.length) {
        const charts = [...pendingCharts];
        pendingCharts.length = 0;
        return { ...item, content: { ...item.content, charts } };
      }
      pendingCharts.length = 0;
    }

    return item;
  });
}
